import { useEffect, useState } from "react";
import type {
  AdSettings,
  AdSlotKey,
  AdZones,
  AdminLabelAxis,
  AdminLabelImportSummary,
  AdminServerInfo,
  SystemSettings as Settings,
  WallSettings,
} from "@gooner/shared";
import {
  AD_SLOTS,
  MAX_CLIPS_PER_COLLECTION_FREE,
  MAX_COLLECTIONS_FREE,
  MAX_REGIONS_FREE,
  MAX_SCREENS_FREE,
  MAX_VIEWS_FREE,
  MIN_CHARGE_CENTS,
  centsLabel,
} from "@gooner/shared";
import { adminApi } from "../lib/api";
import { fmtBytes } from "../control/lib/format";
import { Button, Panel, TextInput, Toggle, cx } from "../control/ui/primitives";

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = d ? [`${d}d`, `${h}h`, `${m}m`] : h ? [`${h}h`, `${m}m`] : [`${m}m`, `${s}s`];
  return parts.join(" ");
}

export function SystemSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [server, setServer] = useState<AdminServerInfo | null>(null);
  const [vaultTotals, setVaultTotals] = useState<{ usedBytes: number; budgetBytes: number } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getSettings()
      .then((r) => {
        if (cancelled) return;
        setSettings(r.settings);
        setServer(r.server);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    // The vault's used-against-budget rides beside the settings, not inside them: it is
    // a live sum, not a knob, and a failure here is a missing number, never a load error.
    adminApi
      .vaultTotals()
      .then((t) => {
        if (!cancelled) setVaultTotals(t);
      })
      .catch(() => {
        if (!cancelled) setVaultTotals(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function patch(next: Partial<Settings>) {
    setSaving(true);
    setSavedAt(false);
    try {
      const r = await adminApi.putSettings(next);
      setSettings(r.settings);
      setServer(r.server);
      setSavedAt(true);
    } catch {
      // Reload authoritative state so the UI can't drift from the server.
      const r = await adminApi.getSettings().catch(() => null);
      if (r) setSettings(r.settings);
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return <p className="text-sm text-red-400">Failed to load settings: {loadError}</p>;
  }
  if (!settings || !server) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-zinc-100">System</h1>
        <span
          className={cx(
            "text-xs transition-opacity",
            saving ? "text-zinc-400 opacity-100" : savedAt ? "text-emerald-400 opacity-100" : "opacity-0",
          )}
        >
          {saving ? "Saving…" : "Saved"}
        </span>
      </header>

      <Panel title="Server">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Stat label="Version" value={server.version} />
          <Stat label="Uptime" value={formatUptime(server.uptimeSec)} />
          <Stat label="Node" value={server.nodeVersion} />
          <Stat label="Platform" value={server.platform} />
          <Stat label="Data file" value={server.dataFile} mono title={server.dataFile} />
        </dl>
      </Panel>

      <Panel title="Age gate">
        <div className="space-y-3">
          <Toggle
            label="Require birthday verification"
            checked={settings.ageGate.enabled}
            onChange={(v) => patch({ ageGate: { ...settings.ageGate, enabled: v } })}
          />
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Minimum age</span>
            <div className="w-24">
              <TextInput
                type="number"
                min={0}
                max={130}
                value={settings.ageGate.minAge}
                disabled={!settings.ageGate.enabled}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    ageGate: { ...settings.ageGate, minAge: Number(e.target.value) },
                  })
                }
                onBlur={(e) => {
                  const n = Math.min(130, Math.max(0, Math.round(Number(e.target.value) || 0)));
                  patch({ ageGate: { ...settings.ageGate, minAge: n } });
                }}
                className="text-center"
              />
            </div>
          </label>
          <p className="text-xs text-zinc-500">
            Applies to new visitors. Anyone who already passed keeps access until they clear
            their browser storage.
          </p>
        </div>
      </Panel>

      <Panel title="Wall playback">
        <div className="space-y-3">
          {/* Issues #13/#14 (2026-10-28): what weak devices are asked to decode. Read
              through displaySettings(), which clamps — a bad value falls back, never blanks. */}
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Highest rung on phones</span>
            <select
              value={settings.display.phoneRungCeiling}
              onChange={(e) =>
                patch({ display: { ...settings.display, phoneRungCeiling: Number(e.target.value) } })
              }
              className="rounded-md border border-[var(--color-edge)] bg-[var(--color-ink)] px-2 py-1 text-sm text-zinc-100"
            >
              <option value={0}>No limit</option>
              <option value={360}>360p</option>
              <option value={720}>720p</option>
              <option value={1080}>1080p</option>
            </select>
          </label>
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Pixel-ratio cap on phones</span>
            <div className="w-24">
              <TextInput
                type="number"
                min={1}
                max={3}
                step={0.25}
                value={settings.display.phoneDprCap}
                onChange={(e) =>
                  setSettings({ ...settings, display: { ...settings.display, phoneDprCap: Number(e.target.value) } })
                }
                onBlur={(e) => {
                  const n = Math.min(3, Math.max(1, Number(e.target.value) || 1.5));
                  patch({ display: { ...settings.display, phoneDprCap: n } });
                }}
                className="text-center"
              />
            </div>
          </label>
          <Toggle
            label="Decode budget"
            checked={settings.display.decodeBudget}
            onChange={(v) => patch({ display: { ...settings.display, decodeBudget: v } })}
          />
          <p className="text-xs text-zinc-500">
            The decode budget probes each device, then steps video quality and how many tiles
            play at once down when frames drop, and back up when playback is clean. It is per
            screen and lasts the browser session; off, every device decodes the same load as
            before. A phone is a touch screen whose short side is 600px or less (tablets are
            not). Open screens pick a change up on their next load.
          </p>
        </div>
      </Panel>

      <Panel title="Goon Fuel">
        <Toggle
          label="Show the floating player on control screens"
          checked={settings.goonFuel.enabled}
          onChange={(v) => patch({ goonFuel: { enabled: v } })}
        />
      </Panel>

      <Panel title="GOONCHA">
        <div className="space-y-2">
          <Toggle
            label="Require the robot check to register and sign in"
            checked={settings.gooncha.enabled}
            onChange={(v) => patch({ gooncha: { enabled: v } })}
          />
          <p className="text-xs text-zinc-500">
            This toggle is the kill switch: off, auth asks for no token instantly — no
            deploy, no client update. Flip it on only after a verified live solve.
          </p>
        </div>
      </Panel>

      <Panel title="GoonLords Premium">
        <div className="space-y-3">
          <Toggle
            label="Sell Premium (the Premium dialog's checkout CTA, menu rows, upsell prompts)"
            checked={settings.gold.enabled}
            onChange={(v) => patch({ gold: { ...settings.gold, enabled: v } })}
          />
          <Toggle
            label={`Enforce Basic caps (${MAX_SCREENS_FREE} screens · ${MAX_VIEWS_FREE} views · ${MAX_COLLECTIONS_FREE} collections of ${MAX_CLIPS_PER_COLLECTION_FREE} clips · ${MAX_REGIONS_FREE} regions)`}
            checked={settings.gold.capsEnabled}
            onChange={(v) => patch({ gold: { ...settings.gold, capsEnabled: v } })}
          />
          <p className="text-xs text-zinc-500">
            Caps bind everyone who isn't Premium — no grandfathering. Nothing existing is
            taken away: counts are keep-what-you-have, and over-cap views stay editable
            without growing.
          </p>
          {/* The daily wall allowance rides the same switch: capsEnabled off = unlimited. */}
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Basic wall allowance, wall-minutes a day (0 = unlimited)</span>
            <div className="w-24">
              <TextInput
                value={String(settings.gold.wallMinutesFree)}
                onChange={(e) =>
                  setSettings({ ...settings, gold: { ...settings.gold, wallMinutesFree: Number(e.target.value) || 0 } })
                }
                onBlur={(e) =>
                  patch({ gold: { ...settings.gold, wallMinutesFree: Math.max(0, Math.round(Number(e.target.value) || 0)) } })
                }
              />
            </div>
          </label>
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Tiles a spent screen keeps</span>
            <div className="w-24">
              <TextInput
                value={String(settings.gold.degradedRegions)}
                onChange={(e) =>
                  setSettings({ ...settings, gold: { ...settings.gold, degradedRegions: Number(e.target.value) || 4 } })
                }
                onBlur={(e) =>
                  patch({ gold: { ...settings.gold, degradedRegions: Math.min(16, Math.max(1, Math.round(Number(e.target.value) || 4))) } })
                }
              />
            </div>
          </label>
          <p className="text-xs text-zinc-500">
            Wall-minutes = minutes watched × tiles playing (960 is sixteen tiles for an hour,
            four for four). Spent, the screen shrinks to the tiles above with sound off until
            midnight UTC; the Community and Premium Walls never count. Aim for 5–10% of engaged
            free users meeting it.
          </p>
          <label className="flex items-center justify-between gap-4">
            <span className="text-sm text-zinc-300">Checkout link (hosted)</span>
            <div className="w-64">
              <TextInput
                value={settings.gold.paymentUrl}
                placeholder="https://…"
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    gold: { ...settings.gold, paymentUrl: e.target.value },
                  })
                }
                onBlur={(e) => patch({ gold: { ...settings.gold, paymentUrl: e.target.value.trim() } })}
              />
            </div>
          </label>
          <p className="text-xs text-zinc-500">
            The Patreon subscribe link. The dialog quotes ${settings.gold.price30d}/month
            from the 30-day slot; the other pass prices are the legacy ladder and are no
            longer shown anywhere (passes are being phased out).
          </p>
        </div>
      </Panel>

      <Panel title="The Feed">
        <div className="space-y-3">
          <Toggle
            label="Feed on (members post clips, crown, comment; the menu entry appears — ships off like everything that publishes a stranger's words)"
            checked={settings.feed.enabled}
            onChange={(v) => patch({ feed: { enabled: v } })}
          />
          <p className="text-xs text-zinc-400">
            Uploads posted on the Feed ride the wall's review queue, so the vault and the
            wall's upload switch govern those. A flip reaches an open tab on its next load.
          </p>
        </div>
      </Panel>

      <Panel title="Goon Rooms">
        <div className="space-y-3">
          <Toggle
            label="Rooms on (members open rooms, watch together, chat; the menu entry appears — ships off like the Feed)"
            checked={settings.rooms.enabled}
            onChange={(v) => patch({ rooms: { ...settings.rooms, enabled: v } })}
          />
          <Toggle
            label="House rooms on (server-run rooms listed under &quot;Always on&quot; on /rooms; off closes them for whoever is in them)"
            checked={settings.rooms.houseRooms}
            onChange={(v) => patch({ rooms: { ...settings.rooms, houseRooms: v } })}
          />
          <Toggle
            label="Chat in house rooms (nobody hosts them, so nobody can kick or ban there; off hides the chat and refuses new lines)"
            checked={settings.rooms.houseChat}
            onChange={(v) => patch({ rooms: { ...settings.rooms, houseChat: v } })}
          />
          <p className="text-xs text-zinc-400">
            A room lives while its host is in it and closes when they leave. A flip reaches an
            open tab on its next load.
          </p>
        </div>
      </Panel>

      <Panel title="The vault">
        <div className="space-y-3">
          <Toggle
            label="Vault on (accounts can upload their own private media; ships off because it costs disk)"
            checked={settings.vault.enabled}
            onChange={(v) => patch({ vault: { ...settings.vault, enabled: v } })}
          />
          {/* Used against the budget — the operator's one number for "is disk safe". */}
          <p className="text-xs text-zinc-400">
            {vaultTotals
              ? `${fmtBytes(vaultTotals.usedBytes)} used of a ${fmtBytes(vaultTotals.budgetBytes)} budget`
              : "Usage unavailable."}
          </p>
          {/* Every number is bytes on the wire and MEGABYTES here: an operator thinks in MB,
              the server thinks in bytes, and the conversion lives in this one grid. */}
          <VaultNumbers
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["baseBytesFree", "Basic starts with (MB)"],
              ["maxBytesFree", "Basic ceiling (MB)"],
              ["baseBytesPremium", "Premium starts with (MB)"],
              ["maxBytesPremium", "Premium ceiling (MB)"],
              ["stepBytes", "One step buys (MB)"],
              ["maxFileBytes", "Max file (MB)"],
              ["diskBudgetBytes", "Global budget (MB)"],
            ]}
          />
          <FapsLikeNumbers
            block="vault"
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["stepPriceFaps", "One step costs (Faps)"],
              ["maxSeconds", "Max video length (s)"],
            ]}
          />
          <p className="text-xs text-zinc-500">
            An upload is its master: no renditions, no preview, no tags, no framing, and it
            never leaves this box. Uploads live only in the owner&rsquo;s &ldquo;Your
            Uploads&rdquo; and play only on the owner&rsquo;s own screens. The global budget
            refuses every upload with a 507 once it is spent, whatever the accounts&rsquo;
            own quotas say.
          </p>
        </div>
      </Panel>

      <Panel title="Faps">
        <div className="space-y-3">
          <Toggle
            label="Faps on (the crown pays, the chest opens, Premium draws a stipend, and the wall sells in Faps instead of dollars — open tabs pick this up on their next page load)"
            checked={settings.faps.enabled}
            onChange={(v) => patch({ faps: { ...settings.faps, enabled: v } })}
          />
          {/* The rate card. Whole numbers; blur saves, the wall panel's idiom. Every one
              of these is quoted to people by the wallet dialog, so a change here is a
              change in what the site promises. */}
          <FapsNumbers
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["welcomeGrant", "Welcome grant"],
              ["fapReward", "Crown pays"],
              ["fapDailyCap", "Crown cap / day (clips)"],
              ["fapRewardPremium", "Premium crown pays"],
              ["fapDailyCapPremium", "Premium crown cap / day (clips)"],
              ["watchThresholdMinutes", "Watch minutes to earn"],
              ["watchReward", "Watch-time pays (flat, once/day)"],
              ["watchMultiplierPremium", "Premium watch-time ×"],
              ["stipendPerEntitledCent", "Stipend per cent of tier"],
            ]}
          />
          <p className="text-xs font-medium text-zinc-300">Daily quests</p>
          <FapsNumbers
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["questCount", "Quests assigned / day"],
              ["questBundleReward", "Bundle pays (all quests done)"],
              ["questMultiplierPremium", "Premium bundle ×"],
              ["questTargetFapClips", "Target: clips fapped"],
              ["questTargetScrollPages", "Target: pages scrolled"],
              ["questTargetEdges", "Target: edges"],
              ["questTargetBattleRounds", "Target: Goonette of the Hill rounds"],
              ["questTargetWatchClips", "Target: distinct clips watched"],
              ["questTargetWatchViewMinutes", "Target: minutes on today's watch pick"],
              ["questTargetRoomMinutes", "Target: minutes in a Goon Room"],
              ["questTargetFeedFaps", "Target: Feed posts to fap"],
              ["questTargetCollectionAdds", "Target: clips to add to a collection"],
              ["questTargetLoops", "Target: clips to loop on a screen"],
            ]}
          />
          <p className="text-xs font-medium text-zinc-300">Daily chest</p>
          <FapsNumbers
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["chestPrizeSmall", "Small prize"],
              ["chestPrizeLarge", "Large prize"],
              ["chestPrizeBig", "Big prize"],
              ["chestLargePct", "Large %"],
              ["chestBigPct", "Big %"],
              ["chestMultiplierPremium", "Premium chest ×"],
            ]}
          />
          <p className="text-xs font-medium text-zinc-300">Wall prices in Faps</p>
          <FapsNumbers
            settings={settings}
            setSettings={setSettings}
            patch={patch}
            rows={[
              ["wallPriceMarquee", "Marquee"],
              ["wallPriceMedium", "Medium"],
              ["wallPriceSmall", "Small"],
            ]}
          />
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">
              Where codes are sold (the Patreon shop URL — blank falls back to the Premium link)
            </span>
            <TextInput
              value={settings.faps.shopUrl}
              onChange={(e) => setSettings({ ...settings, faps: { ...settings.faps, shopUrl: e.target.value } })}
              onBlur={(e) => patch({ faps: { ...settings.faps, shopUrl: e.target.value.trim() } })}
              placeholder="https://www.patreon.com/…"
            />
          </label>
          <p className="text-xs text-zinc-500">
            With Faps on, both walls ignore their dollar ladders below and their free claims (they sell off the one Faps ladder above); the
            chest is nine tiles, each hiding the big prize at Big %, the large one at Large %,
            the small one otherwise. Codes are minted in the Faps section. Every account gets
            the same number of daily quests, drawn at random from a fixed pool (fap N clips,
            scroll N Infinite-Scroll pages, edge N times, bid on a wall square, save a view);
            finishing every assigned quest that day pays the bundle once, doubled for Premium
            — no quest pays on its own.
          </p>
        </div>
      </Panel>

      {/* Two walls, one panel component: the same knobs with different numbers, so a
          rule that lives in the panel (the processor floor, the multiplier bound) binds
          both boards. Only the Community Wall carries the free-pool copy about seeding;
          the Premium Wall says who may claim. */}
      <WallPanel
        title="Community Wall"
        wall={settings.wall}
        note={null}
        uploads={{
          enabled: settings.wall.uploadsEnabled,
          review: settings.wall.reviewUploads,
          onPatch: (w) => patch({ wall: { ...settings.wall, ...w } }),
        }}
        onEdit={(w) => setSettings({ ...settings, wall: { ...settings.wall, ...w } })}
        onPatch={(w) => patch({ wall: { ...settings.wall, ...w } })}
      />
      <WallPanel
        title="Premium Wall"
        wall={settings.premiumWall}
        note="Only GoonLords Premium members can claim squares here; everyone can watch. Same board, same rules, priced on its own."
        onEdit={(w) => setSettings({ ...settings, premiumWall: { ...settings.premiumWall, ...w } })}
        onPatch={(w) => patch({ premiumWall: { ...settings.premiumWall, ...w } })}
      />

      <LabelshopPanel
        importAxes={settings.labelshop.importAxes}
        onPatch={(importAxes) => patch({ labelshop: { importAxes } })}
      />

      <AdsPanel ads={settings.ads} onPatch={(a) => patch({ ads: { ...settings.ads, ...a } })} />
    </div>
  );
}

/**
 * Labelshop → catalog. Which labelshop axes are LIVE on the site: an approved label on an
 * axis switched on here becomes a `manual` tag on the clip within a few minutes, replacing
 * the model's value. An axis switched off stays in labelshop and the truth journal only —
 * Body position, for one, until its model is finished. The list is the operator's call, not
 * derived from which heads happen to be deployed.
 */
/**
 * One wall's commerce knobs. `onEdit` mirrors a keystroke into local state (so the input
 * shows what was typed); `onPatch` commits a validated value to the server on blur or
 * toggle — the same split the panel had when there was one wall.
 */
function WallPanel({
  title,
  wall,
  note,
  uploads,
  onEdit,
  onPatch,
}: {
  title: string;
  wall: WallSettings;
  /** One sentence that is true of this wall and not the other, or null. */
  note: string | null;
  /** The upload switches — the Community Wall's block alone carries them (one switch
   *  governs uploads on every wall AND the Feed); absent on the Premium Wall's panel. */
  uploads?: { enabled: boolean; review: boolean; onPatch: (w: { uploadsEnabled?: boolean; reviewUploads?: boolean }) => void };
  onEdit: (w: Partial<WallSettings>) => void;
  onPatch: (w: Partial<WallSettings>) => void;
}) {
  return (
    <Panel title={title}>
      <div className="space-y-3">
        {note ? <p className="text-xs text-zinc-400">{note}</p> : null}
        {uploads ? (
          <>
            <Toggle
              label="Uploads on (people can put their own files on a square or a Feed post; needs the vault on too)"
              checked={uploads.enabled}
              onChange={(v) => uploads.onPatch({ uploadsEnabled: v })}
            />
            <Toggle
              label="Review uploads before they publish (off = an upload is live the moment it is placed or posted; the Wall queue still lists it, and a reject still bans the clip, pulls it everywhere and ends the account)"
              checked={uploads.review}
              onChange={(v) => uploads.onPatch({ reviewUploads: v })}
            />
          </>
        ) : null}
        <Toggle
          label="Sell wall squares (the buy dialog's checkout — the board itself always renders)"
          checked={wall.enabled}
          onChange={(v) => onPatch({ enabled: v })}
        />
        <Toggle
          label="Free claims at the floor (a square whose take-price has decayed to its base is free — one per account per day on this wall; a freebie decays like a floor-price sale)"
          checked={wall.freeClaims}
          onChange={(v) => onPatch({ freeClaims: v })}
        />
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              ["priceMarquee", "Marquee $"],
              ["priceMedium", "Medium $"],
              ["priceSmall", "Small $"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block">
              <span className="mb-1 block text-xs text-zinc-400">{label}</span>
              <TextInput
                value={String(wall[key])}
                onChange={(e) => onEdit({ [key]: Number(e.target.value) || 0 })}
                onBlur={(e) => {
                  const n = Math.round((Number(e.target.value) || 0) * 100) / 100;
                  // The processor's floor, enforced here AND in sanitizePatch: below it
                  // the square is unsellable and the buyer, not the operator, finds out.
                  if (Math.round(n * 100) >= MIN_CHARGE_CENTS) onPatch({ [key]: n });
                }}
                className="text-center"
              />
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Takeover multiplier</span>
            <TextInput
              value={String(wall.takeoverMultiplier)}
              onChange={(e) => onEdit({ takeoverMultiplier: Number(e.target.value) || 0 })}
              onBlur={(e) => {
                const n = Math.round((Number(e.target.value) || 0) * 100) / 100;
                if (n >= 1) onPatch({ takeoverMultiplier: n });
              }}
              className="text-center"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Decay days</span>
            <TextInput
              value={String(wall.decayDays)}
              onChange={(e) => onEdit({ decayDays: Number(e.target.value) || 0 })}
              onBlur={(e) => {
                const n = Math.round((Number(e.target.value) || 0) * 100) / 100;
                if (n > 0) onPatch({ decayDays: n });
              }}
              className="text-center"
            />
          </label>
        </div>
        {/* The bidding sinks (Faps only). Ship at 0 / off. */}
        <div className="grid grid-cols-2 items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Displaced cut %</span>
            <TextInput
              value={String(wall.displacedCutPct)}
              onChange={(e) => onEdit({ displacedCutPct: Number(e.target.value) || 0 })}
              onBlur={(e) => {
                const n = Math.round(Number(e.target.value) || 0);
                if (n >= 0 && n <= 100) onPatch({ displacedCutPct: n });
              }}
              className="text-center"
            />
          </label>
          <Toggle
            label="Let holders defend"
            checked={wall.defendEnabled}
            onChange={(v) => onPatch({ defendEnabled: v })}
          />
        </div>
        <p className="text-xs text-zinc-500">
          Bidding (Faps only): a takeover pays the displaced holder this % of the winning
          price back as Faps — 0 is a pure loss. With defend on, a holder can re-buy their
          own square at its current price to reset the clock. Both ship off.
        </p>
        <p className="text-xs text-zinc-500">
          Minimum {centsLabel(MIN_CHARGE_CENTS)} a square — the processor's card
          minimum. Cheaper than this still mints, but the checkout shows no card button
          and the buyer can only pay in crypto. A lower number is not saved.
        </p>
        <p className="text-xs text-zinc-500">
          A takeover starts at multiplier × what the holder paid, falls to their price
          over the decay days, then on down to the square's base price over the same
          again — computed live, so edits reprice the board immediately. Placements
          themselves are in the Wall section.
        </p>
      </div>
    </Panel>
  );
}

function LabelshopPanel({
  importAxes,
  onPatch,
}: {
  importAxes: string[];
  onPatch: (axes: string[]) => void;
}) {
  const [axes, setAxes] = useState<AdminLabelAxis[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<"dry" | "live" | null>(null);
  const [last, setLast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .labelAxes()
      .then((r) => {
        if (!cancelled) setAxes(r.axes);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [importAxes]);

  const toggle = (key: string, on: boolean) =>
    onPatch(on ? [...importAxes.filter((k) => k !== key), key] : importAxes.filter((k) => k !== key));

  async function run(dryRun: boolean) {
    setRunning(dryRun ? "dry" : "live");
    setLast(null);
    try {
      const r = await adminApi.importLabels({ dryRun });
      setLast(describeImport(r.result));
    } catch (e) {
      setLast(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(null);
    }
  }

  return (
    <Panel title="Labelshop → catalog">
      <div className="space-y-3">
        <p className="text-xs text-zinc-500">
          An approved label on an axis switched on here becomes a <code>manual</code> tag on the
          clip automatically (the panel pulls every few minutes), replacing the model&rsquo;s
          value. Switch an axis on only once it is live on the site.
        </p>
        {error ? <p className="text-xs text-red-300">{error}</p> : null}
        {axes?.map((a) => (
          <Toggle
            key={a.key}
            label={`${a.title} → ${a.namespace}:*  ·  ${a.approved} approved${a.facet ? "" : "  ·  no facet on the site, lands as a keyword"}`}
            checked={importAxes.includes(a.key)}
            onChange={(v) => toggle(a.key, v)}
          />
        ))}
        {axes && axes.length === 0 ? (
          <p className="text-xs text-zinc-500">No labelshop database on this box.</p>
        ) : null}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={running !== null} onClick={() => void run(true)}>
            {running === "dry" ? "Previewing…" : "Preview"}
          </Button>
          <Button size="sm" disabled={running !== null} onClick={() => void run(false)}>
            {running === "live" ? "Importing…" : "Import now"}
          </Button>
        </div>
        {last ? <p className="text-xs text-zinc-400">{last}</p> : null}
      </div>
    </Panel>
  );
}

function describeImport(r: AdminLabelImportSummary | null): string {
  if (!r) return "No labelshop database on this box.";
  const per = Object.entries(r.byAxis)
    .map(([k, v]) => `${k}: +${v.added} ~${v.changed} ^${v.promoted}`)
    .join(" · ");
  return `${r.dryRun ? "Would write" : "Wrote"} ${r.added + r.changed + r.promoted} (${per || "nothing"}); cleared ${r.perActorCleared.length}; skipped off-axis ${r.skipped.axisOff}, can't-tell ${r.skipped.cantTell}, not one person ${r.skipped.castNotOne}, out of vocabulary ${r.skipped.outOfVocabulary}.`;
}

/** Human labels for the slots, and where each one actually appears. */
const AD_SLOT_LABELS: Record<AdSlotKey, { label: string; hint: string }> = {
  browseInFeed: { label: "Browse in-feed", hint: "300 × 250 — in the catalog grid on /browse" },
  footer: { label: "Site footer", hint: "300 × 100 — below the page content" },
  browseSidebar: {
    label: "Browse sidebar",
    hint: "160 × 600 — under the keyword groups, desktop only",
  },
  collectionsInFeed: {
    label: "Collections",
    hint: "300 × 250 — between the community shelf and Editor's Choice on /collections. Blank uses the Browse in-feed zone",
  },
  feedInFeed: {
    label: "Feed",
    hint: "300 × 250 — after the third post on /feed, once per page. Blank uses the Browse in-feed zone",
  },
  roomsList: {
    label: "Rooms",
    hint: "300 × 250 — at the end of /rooms, after Open a room. Blank uses the Browse in-feed zone",
  },
  collectionPage: {
    label: "Public collection",
    hint: "300 × 250 — after the 15th clip on a public collection (/browse/:id). Blank uses the Browse in-feed zone",
  },
  viewsRail: {
    label: "Views rail",
    hint: "300 × 250 — under the phone-QR card on /views, desktop only (Goon Fuel may overlap it on short viewports)",
  },
};

/**
 * Advertising. Zone ids live in settings rather than the bundle so a creative that
 * should not be on this site can be pulled without a rebuild or a redeploy.
 */
function AdsPanel({ ads, onPatch }: { ads: AdSettings; onPatch: (p: Partial<AdSettings>) => void }) {
  const [draft, setDraft] = useState<AdZones>(ads.zones);
  // Follow the server whenever it hands back a new authoritative value (including the
  // reload after a failed save), but leave the field alone while it is being typed in.
  useEffect(() => setDraft(ads.zones), [ads.zones]);

  return (
    <Panel title="Advertising">
      <div className="space-y-3">
        <Toggle
          label="Serve JuicyAds zones"
          checked={ads.enabled}
          onChange={(v) => onPatch({ enabled: v })}
        />
        {AD_SLOTS.map((slot) => (
          <label key={slot} className="flex items-center justify-between gap-4">
            <span className="min-w-0">
              <span className="block text-sm text-zinc-300">{AD_SLOT_LABELS[slot].label}</span>
              <span className="block text-xs text-zinc-500">{AD_SLOT_LABELS[slot].hint}</span>
            </span>
            <div className="w-40 shrink-0">
              <TextInput
                inputMode="numeric"
                placeholder="Zone id"
                value={draft[slot] ?? ""}
                disabled={!ads.enabled}
                onChange={(e) => setDraft({ ...draft, [slot]: e.target.value })}
                onBlur={() => onPatch({ zones: draft })}
                className="text-center font-mono"
              />
            </div>
          </label>
        ))}
        <p className="text-xs text-zinc-500">
          Numeric zone ids from the JuicyAds panel. Leave one blank to switch off just that
          slot. Ads never render on the display screens, on the legal pages, before the age
          gate, or while safe mode is on — in safe mode the slot shows an empty grey box and
          contacts no ad server.
        </p>
      </div>
    </Panel>
  );
}

function Stat({
  label,
  value,
  mono,
  title,
}: {
  label: string;
  value: string;
  mono?: boolean;
  title?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</dt>
      <dd className={cx("truncate text-zinc-200", mono && "font-mono text-xs")} title={title}>
        {value}
      </dd>
    </div>
  );
}

const MB = 1024 * 1024;

/** The vault's byte knobs, shown and typed in whole megabytes: the wire is bytes, the
 *  operator is not. Local edit, save on blur, refuse a non-number or a negative. */
function VaultNumbers({
  settings,
  setSettings,
  patch,
  rows,
}: {
  settings: Settings;
  setSettings: (s: Settings) => void;
  patch: (p: Partial<Settings>) => void;
  rows: ReadonlyArray<
    readonly [
      Extract<
        keyof Settings["vault"],
        "baseBytesFree" | "baseBytesPremium" | "maxBytesFree" | "maxBytesPremium" | "stepBytes" | "maxFileBytes" | "diskBudgetBytes"
      >,
      string,
    ]
  >;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {rows.map(([key, label]) => (
        <label key={key} className="block">
          <span className="mb-1 block text-xs text-zinc-400">{label}</span>
          <TextInput
            value={String(Math.round(settings.vault[key] / MB))}
            onChange={(e) =>
              setSettings({ ...settings, vault: { ...settings.vault, [key]: (Number(e.target.value) || 0) * MB } })
            }
            onBlur={(e) => {
              const n = Math.round(Number(e.target.value));
              if (Number.isFinite(n) && n >= 0) patch({ vault: { ...settings.vault, [key]: n * MB } });
            }}
            className="text-center"
          />
        </label>
      ))}
    </div>
  );
}

/** FapsNumbers over a block key — the same whole-number grid for the vault's two
 *  non-byte knobs (a price in Faps, a length in seconds). */
function FapsLikeNumbers({
  block,
  settings,
  setSettings,
  patch,
  rows,
}: {
  block: "vault";
  settings: Settings;
  setSettings: (s: Settings) => void;
  patch: (p: Partial<Settings>) => void;
  rows: ReadonlyArray<readonly [Extract<keyof Settings["vault"], "stepPriceFaps" | "maxSeconds">, string]>;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {rows.map(([key, label]) => (
        <label key={key} className="block">
          <span className="mb-1 block text-xs text-zinc-400">{label}</span>
          <TextInput
            value={String(settings[block][key])}
            onChange={(e) =>
              setSettings({ ...settings, [block]: { ...settings[block], [key]: Number(e.target.value) || 0 } })
            }
            onBlur={(e) => {
              const n = Math.round(Number(e.target.value));
              if (Number.isFinite(n) && n >= 0) patch({ [block]: { ...settings[block], [key]: n } });
            }}
            className="text-center"
          />
        </label>
      ))}
    </div>
  );
}

/** A grid of whole-number knobs for the Faps block: local edit, save on blur, refuse
 *  a non-number or a negative rather than saving a surprise. */
function FapsNumbers({
  settings,
  setSettings,
  patch,
  rows,
}: {
  settings: Settings;
  setSettings: (s: Settings) => void;
  patch: (p: Partial<Settings>) => void;
  rows: ReadonlyArray<readonly [Exclude<keyof Settings["faps"], "enabled" | "shopUrl">, string]>;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {rows.map(([key, label]) => (
        <label key={String(key)} className="block">
          <span className="mb-1 block text-xs text-zinc-400">{label}</span>
          <TextInput
            value={String(settings.faps[key])}
            onChange={(e) =>
              setSettings({ ...settings, faps: { ...settings.faps, [key]: Number(e.target.value) || 0 } })
            }
            onBlur={(e) => {
              const n = Math.round(Number(e.target.value));
              if (Number.isFinite(n) && n >= 0) patch({ faps: { ...settings.faps, [key]: n } });
            }}
            className="text-center"
          />
        </label>
      ))}
    </div>
  );
}
